import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, type OnInit, signal, type WritableSignal } from '@angular/core';
import { FormBuilder, type FormControl, type FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';

import { AuthContext } from ':core/context/auth.context';
import { AuthHttpService } from ':core/http-services/auth.http-service';
import { SvgIcon } from ':shared/enums';
import { isJwtTokenInLocalStorage } from ':shared/utils/jwt-token-local-storage-functions';
import { createUserIdentity } from ':shared/utils/manage-user-uuid';

interface InformationForm {
    email: FormControl<string | null>;
    password: FormControl<string | null>;
}

@Component({
    selector: 'app-diagnostic-login-form',
    standalone: true,
    imports: [
        NgClass,
        FormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatIconModule,
        ReactiveFormsModule,
        TranslateModule,
        MatFormFieldModule,
        MatInputModule,
    ],
    templateUrl: './login-form.component.html',
    styleUrl: './login-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginFormComponent implements OnInit {
    private readonly _formBuilder: FormBuilder = inject(FormBuilder);
    private readonly _router = inject(Router);
    private readonly _authHttpService = inject(AuthHttpService);
    private readonly _authContext = inject(AuthContext);

    readonly SvgIcon = SvgIcon;

    readonly isPasswordVisible: WritableSignal<boolean> = signal(false);

    readonly loginFormGroup: FormGroup<InformationForm> = this._formBuilder.group({
        email: [null as string | null, [Validators.required]],
        password: [null as string | null, [Validators.required]],
    });

    get email(): string | null | undefined {
        return this.loginFormGroup.get('email')?.value;
    }

    get password(): string | null | undefined {
        return this.loginFormGroup.get('password')?.value;
    }

    ngOnInit(): void {
        if (isJwtTokenInLocalStorage() && this._authContext.isTokenValid()) {
            this._router.navigate(['/']);
        }
    }

    onSubmit(): void {
        if (this.loginFormGroup.valid) {
            if (this.email && this.password) {
                this._authHttpService.login({ email: this.email, password: this.password }).subscribe(() => {
                    this._authContext.isTokenValid.set(true);
                    createUserIdentity();
                    this._router.navigate(['/']);
                });
            }
        }
    }

    changePasswordVisibility(): void {
        this.isPasswordVisible.set(!this.isPasswordVisible());
    }
}
